import { React } from '@gravityforms/libraries';
import { post } from '@gravityforms/request';
import { isObject } from '@gravityforms/utils';

const { useCallback, useState } = React;

/**
 * @module useHermes
 * @description A hook to interact with the Hermes API.
 *
 * @since 4.0.5
 *
 * @param {object}   props                  The props for the hook.
 * @param {string}   props.action           The fallback action to use if `queryAction` or `mutationAction` is not provided.
 * @param {Function} props.buildCacheKey    A function to build a cache key.
 * @param {Function} props.addCache         A function to add an item to the cache.
 * @param {object}   props.cache            The cache object.
 * @param {string}   props.endpoint         The Hermes AJAX endpoint.
 * @param {string}   props.mutationAction   The mutation action to use.
 * @param {string}   props.mutationKey      The key in the request body for mutations.
 * @param {string}   props.mutationSecurity The security nonce for mutations.
 * @param {string}   props.queryAction      The query action to use.
 * @param {string}   props.queryKey         The key in the request body for queries.
 * @param {string}   props.querySecurity    The security nonce for queries.
 * @param {string}   props.security         The security nonce to use if `querySecurity` or `mutationSecurity` is not provided.
 * @param {boolean}  props.useCache         Whether to use the cache.
 *
 * @return {object} The Hermes state and actions.
 */
const useHermes = ( {
	action = '',
	buildCacheKey = () => {},
	addCache = () => {},
	cache = {},
	endpoint = '',
	mutationAction = '',
	mutationKey = 'mutation',
	mutationSecurity = '',
	queryAction = '',
	queryKey = 'query',
	querySecurity = '',
	security = '',
	useCache = false,
} ) => {
	const [ isLoading, setIsLoading ] = useState( false );

	if ( ! queryAction && ! action ) {
		throw new Error( 'A `queryAction` or `action` is required.' );
	}
	if ( ! mutationAction && ! action ) {
		throw new Error( 'A `mutationAction` or `action` is required.' );
	}

	const qAction = queryAction || action;
	const mAction = mutationAction || action;
	const qSecurity = querySecurity || security;
	const mSecurity = mutationSecurity || security;

	/**
	 * @function query
	 * @description Performs query on the Hermes API.
	 *
	 * @since 4.0.5
	 *
	 * @param {string|object} queryObj The query object.
	 *
	 * @return {Promise} The query promise.
	 */
	const query = useCallback( ( queryObj ) => {
		// If we are loading, don't do anything.
		if ( isLoading ) {
			return;
		}

		// Validate the query object. Must be a string or an object.
		if ( typeof queryObj !== 'string' && ! isObject( queryObj ) ) {
			throw new Error( 'Invalid query parameter.' );
		}

		// Build the query string.
		let queryString = '';
		if ( typeof queryObj === 'string' ) {
			queryString = queryObj;
		} else if ( isObject( queryObj ) ) {
			queryString = buildQueryString( queryObj );
		}

		// Check cache if using cache.
		const cacheKey = buildCacheKey( queryString );
		if ( useCache ) {
			if ( cache[ cacheKey ] ) {
				return Promise.resolve( cache[ cacheKey ] );
			}
		}

		setIsLoading( true );

		return post( {
			endpoint,
			body: {
				action: qAction,
				security: qSecurity,
				[ queryKey ]: queryString,
			},
		} ).then( ( response ) => {
			setIsLoading( false );
			if ( useCache ) {
				addCache( cacheKey, response );
			}
			return response;
		} );
	}, [ isLoading, useCache, endpoint, qAction, qSecurity, queryKey, buildCacheKey, cache, addCache ] );

	/**
	 * @function mutation
	 * @description Performs mutation on the Hermes API.
	 *
	 * @since 4.0.5
	 *
	 * @param {string|object} mutationObj The mutation object.
	 * @param {string}        type        The mutation type, one of `insert`, `update`, `delete`, or `connect`.
	 *
	 * @return {Promise} The mutation promise.
	 */
	const mutation = useCallback( ( mutationObj, type ) => {
		if ( isLoading ) {
			return;
		}

		if ( typeof mutationObj !== 'string' && ! isObject( mutationObj ) ) {
			throw new Error( 'Invalid mutation parameter.' );
		}

		let mutationString = '';
		if ( typeof mutationObj === 'string' ) {
			mutationString = mutationObj;
		} else {
			switch ( type ) {
				case 'insert':
					mutationString = buildInsertMutationString( mutationObj );
					break;
				case 'update':
					mutationString = buildUpdateMutationString( mutationObj );
					break;
				case 'delete':
					mutationString = buildDeleteMutationString( mutationObj );
					break;
				case 'connect':
					mutationString = buildConnectMutationString( mutationObj );
					break;
				default:
					throw new Error( 'Invalid mutation type' );
			}
		}

		setIsLoading( true );

		return post( {
			endpoint,
			body: {
				action: mAction,
				security: mSecurity,
				[ mutationKey ]: mutationString,
			},
		} ).then( ( response ) => {
			setIsLoading( false );
			return response;
		} );
	}, [ isLoading, endpoint, mAction, mSecurity, mutationKey ] );

	return {
		isLoading,
		query,
		mutation,
	};
};

/**
 * @function buildArgsString
 * @description Build a string representation of a variable for use in Hermes query.
 *
 * @since 4.0.5
 *
 * @param {*} arg The property to convert to a string.
 *
 * @return {string} The string representation of the property.
 */
export const buildArgsString = ( arg ) => {
	if ( arg === null || arg === undefined ) {
		return 'null';
	}
	const type = typeof arg;
	if ( type === 'string' ) {
		// Escape double quotes and backslashes in the string
		const escaped = arg.replace( /"/g, '\\"' ).replace( /\//g, '\\/' );
		return `"${ escaped }"`;
	} else if ( type !== 'object' && ! Array.isArray( arg ) ) {
		// Handle numbers, booleans, etc.
		return String( arg );
	} else if ( Array.isArray( arg ) ) {
		// Handle arrays
		const elements = arg.map( ( e ) => buildArgsString( e ) );
		return '[' + elements.join( ', ' ) + ']';
	}
	// Handle objects
	const parts = [];
	for ( const key in arg ) {
		if ( Object.prototype.hasOwnProperty.call( arg, key ) ) {
			const strKey = String( key );
			const valueStr = buildArgsString( arg[ key ] );
			parts.push( `${ strKey }: ${ valueStr }` );
		}
	}
	return '{' + parts.join( ', ' ) + '}';
};

/**
 * @function buildQueryString
 * @description Build a query string from a query object for use in Hermes query.
 *
 * @since 4.0.5
 *
 * @param {object} queryObj The query object to convert to a string.
 *
 * @return {string} The query string.
 *
 * @example
 * const queryObj = {
 *   _aliases: {
 *     company: 'first_ten_companies',
 *   },
 *   company: {
 *     _args: {
 *       limit: 10,
 *     },
 *     _aliases: {
 *       name: 'business_name',
 *     },
 *     id: true,
 *     name: true,
 *     address: true,
 *     department: {
 *       _aliases: {
 *         id: 'department_id',
 *       },
 *       id: true,
 *       name: true,
 *     },
 *   },
 * };
 *
 * const queryString = buildQueryString( queryObj );
 * // queryString = '{first_ten_companies: company(limit: 10) {id, business_name: name, address, department {department_id: id, name}}}';
 *
 */
export const buildQueryString = ( queryObj = {} ) => {
	/**
	 * @function buildQueryStringRecursive
	 * @description Recursively build a query string from a query object for use in Hermes query.
	 *
	 * @since 4.0.5
	 *
	 * @param {object} obj The query object to convert to a string.
	 *
	 * @return {string} The query string.
	 */
	const buildQueryStringRecursive = ( obj = {} ) => {
		const parts = [];

		let args = '';
		// Process _args if present
		if ( obj._args ) {
			const argsParts = [];
			for ( const argKey in obj._args ) {
				argsParts.push( `${ argKey }: ${ buildArgsString( obj._args[ argKey ] ) }` );
			}
			args = `(${ argsParts.join( ', ' ) })`;
		}

		for ( const key in obj ) {
			if ( key === '_args' || key === '_aliases' ) {
				continue;
			}

			const value = obj[ key ];

			// Determine the alias for this key
			const fieldAlias = obj?._aliases?.[ key ];

			if ( value === true ) {
				parts.push( fieldAlias ? `${ fieldAlias }: ${ key }` : key );
			} else if ( isObject( value ) ) {
				// Recursively build the string for this nested object
				const nestedStr = buildQueryStringRecursive( value );

				// If there's an alias at this level, use it; else, use the key as is.
				if ( fieldAlias ) {
					parts.push( `${ fieldAlias }: ${ key }${ nestedStr }` );
				} else {
					parts.push( `${ key }${ nestedStr }` );
				}
			}
		}

		return `${ args } {${ parts.join( ', ' ) }}`;
	};

	const result = buildQueryStringRecursive( queryObj );

	return result.trim();
};

/**
 * @function buildInsertMutationString
 * @description Build a mutation string for inserting data for use in Hermes mutation.
 *
 * @since 4.0.5
 *
 * @param {object} mutationObj The mutation object to convert to a string.
 *
 * @return {string} The mutation string.
 *
 * @example
 * const mutationObj = {
 *   company: {
 *     _args: {
 *       objects: [ { name: 'My Business', address: '123 Main St' } ],
 *     },
 *     returning: {
 *       id: true,
 *       name: true,
 *       address: true,
 *     },
 *   }
 * };
 *
 * const mutationString = buildInsertMutationString( mutationObj );
 * // mutationString = '{insert_company(objects: [{name: "My Business", address: "123 Main St"}]) {returning {id, name, address}}}';
 *
 */
export const buildInsertMutationString = ( mutationObj ) => {
	const queryObj = Object.keys( mutationObj ).reduce( ( acc, key ) => {
		const { objects = [], returning = {} } = mutationObj[ key ];

		return {
			...acc,
			[ `insert_${ key }` ]: {
				_args: { objects },
				returning,
			},
		};
	}, {} );

	return buildQueryString( queryObj );
};

/**
 * @function buildUpdateMutationString
 * @description Build a mutation string for updating data for use in Hermes mutation.
 *
 * @since 4.0.5
 *
 * @param {object} mutationObj The mutation object to convert to a string.
 *
 * @return {string} The mutation string.
 *
 * @example
 * const mutationObj = {
 *   company: {
 *     id: 1,
 *     _args: {
 *       name: 'My Business',
 *       address: '123 Main St',
 *     },
 *     returning: {
 *       id: true,
 *       name: true,
 *       address: true,
 *     },
 *   },
 * };
 *
 * const mutationString = buildUpdateMutationString( mutationObj );
 * // mutationString = '{update_company(id: 1, name: "My Business", address: "123 Main St") {returning {id, name, address}}}';
 *
 */
export const buildUpdateMutationString = ( mutationObj ) => {
	const queryObj = Object.keys( mutationObj ).reduce( ( acc, key ) => {
		const { id, _args = {}, returning = {} } = mutationObj[ key ];
		if ( ! id ) {
			throw new Error( 'An id is required for update mutations.' );
		}

		return {
			...acc,
			[ `update_${ key }` ]: {
				_args: {
					id,
					..._args,
				},
				returning,
			},
		};
	}, {} );

	return buildQueryString( queryObj );
};

/**
 * @function buildDeleteMutationString
 * @description Build a mutation string for deleting data for use in Hermes mutation.
 *
 * @since 4.0.5
 *
 * @param {object} mutationObj The mutation object to convert to a string.
 * @return {string} The mutation string.
 *
 * @example
 * const mutationObj = {
 *   company: {
 *     id: 1,
 *   },
 * };
 *
 * const mutationString = buildDeleteMutationString( mutationObj );
 * // mutationString = '{delete_company(id: 1) {}}';
 *
 */
export const buildDeleteMutationString = ( mutationObj ) => {
	const queryObj = Object.keys( mutationObj ).reduce( ( acc, key ) => {
		const { id } = mutationObj[ key ];
		if ( ! id ) {
			throw new Error( 'An id is required for delete mutations.' );
		}

		return {
			...acc,
			[ `delete_${ key }` ]: {
				_args: { id },
			},
		};
	}, {} );

	return buildQueryString( queryObj );
};

/**
 * @function buildConnectMutationString
 * @description Build a mutation string for connecting data for use in Hermes mutation.
 *
 * @since 4.0.5
 *
 * @param {object} mutationObj The mutation object to convert to a string.
 *
 * @return {string} The mutation string.
 *
 * @example
 * const mutationObj = {
 *   company_department: {
 *     from: 1,
 *     to: 2,
 *   },
 * };
 *
 * const mutationString = buildConnectMutationString( mutationObj );
 * // mutationString = '{connect_company_department(from: 1, to: 2) {}}';
 *
 */
export const buildConnectMutationString = ( mutationObj ) => {
	const queryObj = Object.keys( mutationObj ).reduce( ( acc, key ) => {
		const { from, to } = mutationObj[ key ];
		if ( ! from || ! to ) {
			throw new Error( 'Both from id and to id are required for connect mutations.' );
		}

		return {
			...acc,
			[ `connect_${ key }` ]: {
				_args: { from, to },
			},
		};
	}, {} );

	return buildQueryString( queryObj );
};

export default useHermes;
